import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FiCreditCard } from 'react-icons/fi';
import { toast } from 'sonner';
import { apiGet, apiPost } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { FormField } from '@/shared/components/ui/FormField';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';

interface MaskedCard {
  maskedNumber: string;
  brand: string;
  holderName: string;
  expiryMonth: number;
  expiryYear: number;
  nickname?: string;
  isDefault: boolean;
}

interface PaymentInfo {
  subscriptionStatus: string | null;
  nextRenewalAt: string | null;
  planName: string | null;
  amount: number | null;
  currency: string;
  defaultCardIndex: number;
  cards: MaskedCard[];
}

interface PaymentDrawerProps {
  company: { id: string; name: string } | null;
  isOpen: boolean;
  onClose: () => void;
}

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (amount: number, currency = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount);

const cardLabel = (c: MaskedCard) => {
  const base = `${c.maskedNumber} · ${c.brand}`;
  return c.nickname ? `${base} (${c.nickname})` : base;
};

export const PaymentDrawer: React.FC<PaymentDrawerProps> = ({ company, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const companyId = company?.id ?? null;
  const [cardIndex, setCardIndex] = useState<number>(0);

  const { data: info, isLoading } = useQuery({
    queryKey: ['admin-company-payment-info', companyId],
    queryFn: () => apiGet<PaymentInfo>(`/companies/${companyId}/payment-info`),
    enabled: !!companyId && isOpen,
  });

  useEffect(() => {
    if (info) setCardIndex(info.defaultCardIndex ?? 0);
  }, [info]);

  const chargeMutation = useMutation({
    mutationFn: () =>
      apiPost<{ message: string; cycleNumber: number; cycleEnd: string }>(
        `/companies/${companyId}/credits/charge-renewal`,
        { cardIndex },
      ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      toast.success(result?.message || 'Pagamento processado');
      onClose();
    },
    // Erros são notificados pelo interceptor global do axios (lib/api) — não duplicar toast aqui.
  });

  const cards = info?.cards ?? [];
  const hasCards = cards.length > 0;
  const amount = info?.amount ?? null;
  const currency = info?.currency ?? 'BRL';

  const handleCharge = () => {
    if (chargeMutation.isPending || !hasCards) return;
    const valor = amount !== null ? formatCurrency(amount, currency) : 'o valor da assinatura';
    if (!window.confirm(`Confirmar cobrança de ${valor} no cartão selecionado? Isso processa o pagamento e inicia um novo ciclo.`)) return;
    chargeMutation.mutate();
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Efetuar pagamento"
      subtitle={company?.name}
      icon={<FiCreditCard className="w-5 h-5" />}
      disabled={chargeMutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={chargeMutation.isPending}>Cancelar</Button>
          <Button onClick={handleCharge} loading={chargeMutation.isPending} disabled={!hasCards}>
            {amount !== null ? `Efetuar cobrança de ${formatCurrency(amount, currency)}` : 'Efetuar cobrança'}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={40} />)}
        </div>
      ) : (
        <div className="space-y-5">
          {/* Resumo da assinatura */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: '#64748b' }}>Assinatura</span>
              <Badge variant={info?.subscriptionStatus === 'active' ? 'success' : 'warning'} size="sm">
                {info?.subscriptionStatus === 'active' ? 'Ativa' : (info?.subscriptionStatus ?? '-')}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: '#64748b' }}>Plano</span>
              <span style={{ color: '#1e293b' }}>{info?.planName ?? '-'}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: '#64748b' }}>Renovação atual</span>
              <span style={{ color: '#1e293b' }}>{formatDate(info?.nextRenewalAt)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: '#64748b' }}>Valor a cobrar</span>
              <span className="font-semibold" style={{ color: '#1e293b' }}>
                {amount !== null ? formatCurrency(amount, currency) : '-'}
              </span>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {/* Seleção de cartão */}
          {hasCards ? (
            <FormField label="Cartão">
              <Select
                value={String(cardIndex)}
                onChange={(v) => setCardIndex(Number(v))}
                options={cards.map((c, i) => ({
                  value: String(i),
                  label: c.isDefault ? `${cardLabel(c)} — principal` : cardLabel(c),
                }))}
              />
            </FormField>
          ) : (
            <p className="text-sm" style={{ color: '#dc2626' }}>
              Nenhum cartão cadastrado para esta empresa.
            </p>
          )}
        </div>
      )}
    </Drawer>
  );
};
